import { MessageSquareIcon, ZapIcon } from "lucide-react";
import type { ElementType } from "react";

import { cn } from "@/lib/utils";

export type ShareSessionInteractionMode = "batch" | "chat";

export interface ShareSessionModeSelectorProps {
  mode: ShareSessionInteractionMode;
  onChange: (mode: ShareSessionInteractionMode) => void;
  /** Full row with descriptions (create dialog); compact row (session card). */
  variant?: "full" | "mini";
}

const OPTIONS: { value: ShareSessionInteractionMode; label: string; icon: ElementType }[] = [
  { value: "batch", label: "Batch", icon: ZapIcon },
  { value: "chat", label: "Chat", icon: MessageSquareIcon },
];

export const ShareSessionModeSelector = ({
  mode,
  onChange,
  variant = "full",
}: ShareSessionModeSelectorProps) => {
  if (variant === "mini") {
    return (
      <div className="flex items-center justify-between py-1.5">
        <div className="flex items-center gap-2">
          {mode === "batch" ? (
            <ZapIcon
              className={cn("h-3.5 w-3.5 transition-colors duration-150", "text-blue-500")}
            />
          ) : (
            <MessageSquareIcon
              className={cn("h-3.5 w-3.5 transition-colors duration-150", "text-blue-500")}
            />
          )}
          <span className="text-xs text-gray-600 dark:text-gray-400">Mode</span>
        </div>
        <div className="flex items-center gap-0.5 rounded-lg bg-gray-100 p-0.5 dark:bg-gray-700">
          {OPTIONS.map(opt => {
            const selected = mode === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(opt.value)}
                className={cn(
                  "flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium transition-all duration-150",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40",
                  selected
                    ? "bg-white text-gray-800 shadow-sm dark:bg-gray-600 dark:text-gray-100"
                    : "text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300",
                )}
              >
                <opt.icon className="h-3 w-3" />
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="group flex items-center justify-between rounded-lg px-3 py-2.5 transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-800/50">
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors duration-200",
            "bg-blue-50 text-blue-500 dark:bg-blue-500/10 dark:text-blue-400",
          )}
        >
          {mode === "batch" ? (
            <ZapIcon className="h-3.5 w-3.5" />
          ) : (
            <MessageSquareIcon className="h-3.5 w-3.5" />
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium leading-tight text-gray-800 dark:text-gray-200">Mode</p>
          <p className="mt-0.5 text-xs leading-tight text-gray-400 dark:text-gray-500">
            {mode === "batch" ? "Single input and output" : "Multi-turn conversation"}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-0.5 rounded-lg bg-gray-100/80 p-0.5 dark:bg-gray-800">
        {OPTIONS.map(opt => {
          const selected = mode === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={cn(
                "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-all duration-150",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40",
                selected
                  ? "bg-white text-gray-800 shadow-sm dark:bg-gray-700 dark:text-gray-100"
                  : "text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300",
              )}
            >
              <opt.icon className="h-3 w-3" />
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};
